import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, TooltipTrigger } from '@bcmi-labs/react-components';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import InfoIcon from '../../../common/InfoIcon';
import { ALL_SOCIALS, SocialLogin } from '../../../common/SocialLogin';
import { Input } from '../../../common/Input';
import { Logo } from '../../../common/Logo';
import AlertBox from '../../../common/AlertBox';
import './RegisterForm.scss';
import isEmail from 'validator/lib/isEmail';

const checkUsernameUnique = async (username, t) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/v1/users/byUsername/${username}`, {
      method: 'HEAD',
    });
    if (response.status === 200) {
      return t('register:username_exists');
    }
    return true;
  } catch (error) {
    return true;
  }
};

const RegisterForm = (props) => {
  const { t } = useTranslation('register');

  const { onSubmit, loading, showConsent, customDomainInfo, error, userData } = props;

  const { handleSubmit, errors, control, watch } = useForm({
    defaultValues: userData,
    mode: 'onBlur',
  });

  const [showUsernameField, setShowUsernameField] = useState(false);

  React.useEffect(() => {
    const watchPassword = watch('password');
    if (watchPassword && watchPassword.length >= 8) {
      setShowUsernameField(true);
    }
  }, [watch]);

  return (
    <section className="register">
      <Logo customLogo={customDomainInfo?.organization_logo} />
      <h1 className="arduino-typography-large">
        {t('register:title', { org: customDomainInfo?.organization_name || 'Arduino' })}
      </h1>
      <SocialLogin permitted={customDomainInfo?.social_logins || ALL_SOCIALS} />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-row">
          <Controller
            as={Input}
            name="email"
            control={control}
            defaultValue=""
            placeholder={t('register:email')}
            error={errors.email && errors.email.message}
            isRequired
            rules={{
              required: t('register:fieldRequired'),
              validate: (email) => {
                if (!isEmail(email)) {
                  return t('register:emailInvalid');
                }
              },
            }}
            showErrorMessage
          />
        </div>
        <div className="form-row">
          <Controller
            as={Input}
            type="password"
            name="password"
            control={control}
            infoMsg={t('register:passwordMinLength')}
            defaultValue=""
            placeholder={t('register:password')}
            error={errors.password && errors.password.message}
            isRequired
            rules={{
              required: t('register:fieldRequired'),
              minLength: {
                value: 8,
                message: t('register:passwordMinLength'),
              },
            }}
            showErrorMessage={errors.password && errors.password.type !== 'minLength'}
          />
        </div>
        {showUsernameField && (
          <div className="form-row register__username-row">
            <Controller
              as={Input}
              name="username"
              control={control}
              defaultValue=""
              placeholder={t('register:username')}
              error={errors.username && errors.username.message}
              isRequired
              rules={{
                required: t('register:fieldRequired'),
                pattern: {
                  value: /(^[a-z0-9][a-z0-9_-]{2,}$)/i,
                  message: t('register:usernameInvalid'),
                },
                maxLength: {
                  value: 128,
                  message: t('register:usernameMaxLength'),
                },
                validate: (username) => checkUsernameUnique(username, t),
              }}
              showErrorMessage
            />
            <div className="tooltip-row-wrapper register__username-tooltip">
              <TooltipTrigger
                TriggerElement={
                  <div>
                    <InfoIcon />
                  </div>
                }
                className="password-popover"
                tooltipPlacement="top-start"
                tooltipStrategy="absolute"
                tooltipType={'popover'}
              >
                {t('register:tooltip')}
              </TooltipTrigger>
            </div>
          </div>
        )}
        {showConsent ? (
          <div className="privacy-container">
            <div className="privacy-row">
              <Controller
                render={({ onChange, onBlur, value }) => (
                  <Checkbox
                    label={t('register:privacyMd')}
                    isRequired
                    onBlur={onBlur}
                    onChange={(e) => onChange(e.target.checked)}
                    isSelected={value}
                    error={errors.privacyApproval}
                  />
                )}
                name="privacyApproval"
                control={control}
                defaultValue=""
                rules={{
                  required: t('register:fieldRequired'),
                }}
              />
            </div>
          </div>
        ) : null}
        {error ? <AlertBox message={error} /> : null}
        <Button type="submit" loading={loading} isDisabled={!showUsernameField}>
          {customDomainInfo?.organization_name ? t('register:submitText') : t('register:submitNext')}
        </Button>
      </form>

      <div className="spacer"></div>
    </section>
  );
};

RegisterForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool,
  showConsent: PropTypes.bool.isRequired,
  customDomainInfo: PropTypes.object,
  userData: PropTypes.object,
};

export default RegisterForm;
